import { KeysOfType, unique } from '@epicuro-next/utilities';
import { ActionCreator, createReducer, on } from '@ngrx/store';

import {
  checkBasket,
  checkBasketFailure,
  checkBasketSuccess,
  downloadBasket,
  downloadBasketFailure,
  downloadBasketSuccess,
  groupDtoBasketFailure,
  groupDtoBasketSuccess,
  loadActiveBasket,
  loadActiveBasketFailure,
  loadBasketById,
  loadBasketFailure,
  loadBasketSuccess,
  loadDtoBasketFailure,
  loadMaxDiscountPercentage,
  loadMaxDiscountPercentageSuccess,
  loadMaxDiscountPercentageForMaterials,
  loadMaxDiscountPercentageForMaterialsSuccess,
  loadOpenedBasket,
  manualDiscountsBusyDeleting,
  manualDiscountsBusyUpdating,
  manualDiscountsDeleteFailed,
  manualDiscountsDeleteSuccess,
  manualDiscountsUpdatedFailed,
  manualDiscountsUpdatedSuccess,
  moveBasketSuccess,
  reuseBasket,
  reuseBasketFailure,
  setBasketLoading,
  submitBasket,
  submitBasketFailure,
  submitBasketSuccess,
  switchBasket,
  updateAccountDeliveryClientReferenceNumber,
  updateAccountDeliveryClientReferenceNumberFailure,
  updateAccountDeliveryClientReferenceNumberSuccess,
  updateBasketItems,
  updateBasketItemsFailure,
  updateBasketItemsSuccess,
  updateBasketShops,
  updateBasketShopsFail,
  updateBasketShopsSuccess,
  updateCustomOrderReference,
  updateCustomOrderReferenceFailure,
  updateCustomOrderReferenceSuccess,
  updateDeliveryLineClientReferenceNumber,
  updateDeliveryLineClientReferenceNumberFailure,
  updateDeliveryLineClientReferenceNumberSuccess,
  updateSelectedShippingOption,
  updateSelectedShippingOptionFailed,
  updateSelectedShippingOptionSuccess,
  updateShipInFullOption,
  updateShipInFullOptionFailed,
  updateShipInFullOptionSuccess,
} from '../actions';

export const basketLoadersFeatureKey = 'loaders';

export interface State {
  /** if the basket is loading, temporary changed to number as active basket and opened basket could be loaded */
  loading: number;
  /** Boolean to check if placing the basket */
  placing: boolean;
  /** Boolean to check if checking the basket */
  checking: boolean;
  /** Boolean to check if updating the basket */
  updating: boolean;
  /** Boolean to check if copying the basket */
  copying: boolean;
  /** Boolean to check if basket content is downloading */
  downloading: boolean;
  /** Indicate that selected shipping option is updating */
  updateShippingOption: boolean;
  /** Indicate that basket level PO number is updating */
  updateBasketClientReferenceNumber: boolean;
  /**
   * This is an array of addressIds that are currently updating its custom order reference numbers
   */
  updatingAddressIds: string[];
  /**
   * This is an array of deliveries that are currently updating its custom order reference numbers
   */
  updatingDeliveryIds: string[];
  /** Indicate that selected ship in full option is updating */
  updateShipInFullOption: boolean;
  /** Errors regarding loading of the basket */
  loadingError: string | null;
  busyUpdatingManualDiscounts: boolean;
  busyDeletingManualDiscounts: boolean;
  busyLoadingMaxDiscountPercentageForOrderType: boolean;
  busyLoadingMaxDiscountPercentageForProducts: boolean;
}

export const initialState: State = {
  loading: 0,
  placing: false,
  checking: false,
  updating: false,
  copying: false,
  downloading: false,
  updateShippingOption: false,
  updateBasketClientReferenceNumber: false,
  updatingAddressIds: [],
  updatingDeliveryIds: [],
  updateShipInFullOption: false,
  loadingError: null,
  busyUpdatingManualDiscounts: false,
  busyDeletingManualDiscounts: false,
  busyLoadingMaxDiscountPercentageForOrderType: false,
  busyLoadingMaxDiscountPercentageForProducts: false,
};

export const reducer = createReducer(
  initialState,
  // loading
  on(
    loadBasketSuccess,
    groupDtoBasketSuccess,
    (state): State => ({
      ...state,
      loading: state.loading > 0 ? state.loading - 1 : 0,
    }),
  ),
  on(
    loadBasketById,
    loadActiveBasket,
    loadOpenedBasket,
    switchBasket,
    moveBasketSuccess,
    (state): State => ({
      ...state,
      loading: state.loading + 1,
    }),
  ),
  on(
    loadBasketFailure,
    loadDtoBasketFailure,
    loadActiveBasketFailure,
    groupDtoBasketFailure,
    (state, { error }): State => ({
      ...state,
      loading: state.loading > 0 ? state.loading - 1 : 0,
      loadingError: error ?? null,
    }),
  ),
  on(setBasketLoading, (state, { value }) => ({
    ...state,
    loading: value
      ? state.loading + 1
      : state.loading > 0
      ? state.loading - 1
      : 0,
  })),
  // updating
  on(updateBasketShops, updateBasketItems, (state) => ({
    ...state,
    updating: true,
  })),
  on(
    updateBasketShopsSuccess,
    updateBasketShopsFail,
    updateBasketItemsSuccess,
    updateBasketItemsFailure,
    (state) => ({
      ...state,
      updating: false,
    }),
  ),
  // checking
  on(
    checkBasket,
    (state): State => ({
      ...state,
      checking: true,
    }),
  ),
  on(
    checkBasketSuccess,
    checkBasketFailure,
    (state): State => ({
      ...state,
      checking: false,
    }),
  ),
  // placing
  on(
    submitBasket,
    (state): State => ({
      ...state,
      placing: true,
    }),
  ),
  on(
    submitBasketSuccess,
    submitBasketFailure,
    (state): State => ({
      ...state,
      placing: false,
    }),
  ),
  // downloading
  on(
    downloadBasket,
    (state): State => ({
      ...state,
      downloading: true,
    }),
  ),
  on(
    downloadBasketSuccess,
    downloadBasketFailure,
    (state): State => ({
      ...state,
      downloading: false,
    }),
  ),
  // copying
  on(
    reuseBasket,
    (state): State => ({
      ...state,
      copying: true,
    }),
  ),
  on(
    groupDtoBasketSuccess,
    reuseBasketFailure,
    (state): State => ({
      ...state,
      copying: false,
    }),
  ),
  on(
    updateSelectedShippingOption,
    (state): State => ({
      ...state,
      updateShippingOption: true,
    }),
  ),
  on(
    updateSelectedShippingOptionSuccess,
    updateSelectedShippingOptionFailed,
    (state): State => ({
      ...state,
      updateShippingOption: false,
    }),
  ),
  on(updateCustomOrderReference, (state) => ({
    ...state,
    updateBasketClientReferenceNumber: true,
  })),
  on(
    updateCustomOrderReferenceSuccess,
    updateCustomOrderReferenceFailure,
    (state) => ({
      ...state,
      updateBasketClientReferenceNumber: false,
    }),
  ),
  on(updateAccountDeliveryClientReferenceNumber, (state, { addressId }) => ({
    ...state,
    updatingAddressIds: [...state.updatingAddressIds, addressId].filter(unique),
  })),
  on(
    updateAccountDeliveryClientReferenceNumberFailure,
    updateAccountDeliveryClientReferenceNumberSuccess,
    (state, { addressId }) => ({
      ...state,
      updatingAddressIds: state.updatingAddressIds.filter(
        (id) => id !== addressId,
      ),
    }),
  ),
  on(
    updateDeliveryLineClientReferenceNumber,
    (state, { deliveryId }): State => ({
      ...state,
      updatingDeliveryIds: [...state.updatingDeliveryIds, deliveryId].filter(
        unique,
      ),
    }),
  ),
  on(
    updateDeliveryLineClientReferenceNumberFailure,
    updateDeliveryLineClientReferenceNumberSuccess,
    (state, { deliveryId }): State => ({
      ...state,
      updatingDeliveryIds: state.updatingDeliveryIds.filter(
        (id) => id !== deliveryId,
      ),
    }),
  ),
  on(
    updateShipInFullOption,
    (state): State => ({
      ...state,
      updateShipInFullOption: true,
    }),
  ),
  on(
    updateShipInFullOptionSuccess,
    updateShipInFullOptionFailed,
    (state): State => ({
      ...state,
      updateShipInFullOption: false,
    }),
  ),
  ...createLoadingFlagReducerMethods(
    'busyUpdatingManualDiscounts',
    manualDiscountsBusyUpdating,
    manualDiscountsUpdatedFailed,
    manualDiscountsUpdatedSuccess,
  ),
  ...createLoadingFlagReducerMethods(
    'busyDeletingManualDiscounts',
    manualDiscountsBusyDeleting,
    manualDiscountsDeleteFailed,
    manualDiscountsDeleteSuccess,
  ),
  ...createLoadingFlagReducerMethods(
    'busyLoadingMaxDiscountPercentageForOrderType',
    loadMaxDiscountPercentage,
    loadMaxDiscountPercentageSuccess,
  ),
  ...createLoadingFlagReducerMethods(
    'busyLoadingMaxDiscountPercentageForProducts',
    loadMaxDiscountPercentageForMaterials,
    loadMaxDiscountPercentageForMaterialsSuccess,
  ),
);

type ActionType = ActionCreator<string, (...args: unknown[]) => object>;
type KeysOfBooleanPropertiesInState = KeysOfType<State, boolean>;

/**
 * Creates 2 or more reducer methods to toggle a flag true/false indicating the loading state.
 * @param flag The key to toggle
 * @param loadAction The action that will set it to true
 * @param doneActions The action(s) that will set it to false
 * @returns 2 or more reducer methods.
 */
export function createLoadingFlagReducerMethods(
  flag: KeysOfBooleanPropertiesInState,
  loadAction: ActionType,
  ...doneActions: ActionType[]
) {
  return [
    on(loadAction, (state: State) => {
      return {
        ...state,
        [flag]: true,
      };
    }),
    ...doneActions.map((action) => {
      return on(action, (state: State) => {
        return {
          ...state,
          [flag]: false,
        };
      });
    }),
  ];
}
